import { Libraries } from '@react-google-maps/api';
import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { cn } from '../../lib/utils';
import { CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Skeleton } from './skeleton';

export type Option = Record<'value' | 'label', string> & Record<string, string>;

type AutoCompleteProps = {
  options?: Option[];
  searchIcon?: boolean;
  emptyMessage?: string;
  value?: Option;
  onValueChange?: (value: Option) => void;
  onSelection: (value: { lat: number; lng: number }) => void;
  disabled?: boolean;
  placeholder?: string;
  markerPosition?: { lat: number; lng: number };
  placeId?: string;
  iValue?: string;
  label?: string;
  error?: string;
  required?: boolean;
  setCountry?: (country: string) => void;
};

const libraries: Libraries = ['places'];

export const LocationAutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  onSelection,
  placeId,
  searchIcon,
  iValue,
  label,
  error,
  required,
  setCountry,
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option>(value as Option);
  const [inputValue, setInputValue] = useState<string>(value?.label || iValue || '');

  useEffect(() => {
    setInputValue(iValue || '');
  }, [iValue]);

  useEffect(() => {
    placesService?.getDetails(
      {
        placeId: placeId ?? '',
      },
      (place) => {
        if (place) {
          setInputValue(place?.formatted_address || '');
          setSelected({ value: place.place_id || '', label: place.formatted_address || '' });
          onValueChange?.({ value: place.place_id || '', label: place.formatted_address || '' });
        }
      },
    );
  }, [placeId]);

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } =
    usePlacesService({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      libraries,
      debounce: 300,
    });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setOpen(true);
      }

      // This is not a default behaviour of the <input /> field
      if (event.key === 'Enter' && input.value !== '') {
        const optionToSelect = options?.find((option) => option.label === input.value);
        if (optionToSelect) {
          setSelected(optionToSelect);
          onValueChange?.(optionToSelect);
        }
      }

      if (event.key === 'Escape') {
        input.blur();
      }
    },
    [isOpen, options, onValueChange],
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
    setInputValue(selected?.label);
  }, [selected]);

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      setInputValue(selectedOption.label);
      setSelected(selectedOption);
      onValueChange?.(selectedOption);
      placesService?.getDetails(
        {
          placeId: selectedOption.value,
        },
        (place) => {
          const country =
            place?.address_components?.find((component) => component.types.includes('country'))
              ?.long_name ?? '';
          onSelection?.({
            lat: place?.geometry?.location?.lat() || 0,
            lng: place?.geometry?.location?.lng() || 0,
          });
          if (setCountry) {
            setCountry(country);
          }
        },
      );
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange],
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
    getPlacePredictions({
      input: value,
    });
  };

  return (
    <CommandPrimitive
      onKeyDown={handleKeyDown}
      className={cn('rounded-xl border  relative h-[60px]', error ? 'border-red' : '')}
    >
      {label && (
        <label
          htmlFor={inputRef.current?.id}
          className={cn(
            'absolute left-3 pointer-events-none transition-all duration-200 flex flex-col gap-1 justify-center',
            inputValue || iValue || isOpen
              ? 'text-[12px] top-[7px] text-white'
              : 'text-[15px] top-[17px] text-white',
            placeholder ? 'text-[12px] top-[7px] text-white' : '',
          )}
        >
          <p>
            {label}
            {required ? <span className='text-[#DC1D00]'>*</span> : ''}
          </p>
          <span className='-mt-1 text-[14px] text-white'>
            {inputValue || iValue || isOpen ? '' : placeholder}
          </span>
        </label>
      )}
      <div>
        <CommandInput
          showSearchIcon={searchIcon}
          ref={inputRef}
          value={inputValue}
          onValueChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn('text-sm h-[54px] bg-transparent text-white', label ? 'pt-[28px]' : '')}
        />
      </div>
      <div className='relative mt-1'>
        <div
          className={cn(
            'animate-in fade-in-0 zoom-in-95 absolute top-0.5 z-10 w-full rounded-xl bg-bgPrimary outline-none',
            isOpen && placePredictions.length > 0 ? 'block' : 'hidden',
          )}
        >
          <CommandList className='rounded-lg ring-1 ring-slate-200'>
            {isPlacePredictionsLoading ? (
              <CommandPrimitive.Loading>
                <div className='p-1'>
                  <Skeleton className='h-8 w-full' />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {placePredictions.length > 0 && !isPlacePredictionsLoading ? (
              <CommandGroup>
                {placePredictions.map((option) => {
                  const isSelected = selected?.value === option.place_id;
                  return (
                    <CommandItem
                      key={option.place_id}
                      value={option.description}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onSelect={() =>
                        handleSelectOption({ value: option.place_id, label: option.description })
                      }
                      className={cn(
                        'flex w-full items-center gap-2 text-white aria-selected:text-white aria-selected:bg-[#151525] data-selected:bg-[#151525]',
                        !isSelected ? 'pl-8' : null,
                      )}
                    >
                      {isSelected ? <Check className='w-4' /> : null}
                      {option.description}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {!isPlacePredictionsLoading ? (
              <CommandPrimitive.Empty className='select-none rounded-sm px-2 py-3 text-center text-sm'>
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
      {error && (
        <p
          className='text-xs text-destructive absolute top-[63px]'
          id='phone-input-error'
          aria-live='polite'
        >
          {error}
        </p>
      )}
    </CommandPrimitive>
  );
};
